import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { useQueryClient } from '@tanstack/react-query'
import { databaseKeys } from '@/hooks/useDatabase'
import {
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface CreateProcedureViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onProcedureCreated: () => void
  duplicateFrom?: {
    originalName: string
    newName: string
    definition: unknown
  }
}

interface ProcedureParameter {
  name: string
  type: string
  mode: 'IN' | 'OUT' | 'INOUT'
  defaultValue?: string
}

// Utility function to extract procedure body from CREATE statement
function extractProcedureBody(definition: string): string {
  if (!definition) return ''
  
  console.log('Extracting procedure body from:', `${definition.substring(0, 200)}...`)
  
  // MySQL-style: Extract body from BEGIN ... END blocks
  const mysqlBeginEndMatch = definition.match(/BEGIN\s+([\s\S]*?)\s+END(?:\s*;?\s*$)/i)
  if (mysqlBeginEndMatch && mysqlBeginEndMatch[1]) {
    console.log('Found MySQL BEGIN...END block, body:', `${mysqlBeginEndMatch[1].substring(0, 100)}...`)
    return mysqlBeginEndMatch[1].trim()
  }
  
  // PostgreSQL-style: Handle tagged dollar quoting: AS $tag$ ... $tag$
  const taggedMatch = definition.match(/AS\s+\$([^$\s]+)\$\s*([\s\S]*?)\s*\$\1\$/i)
  if (taggedMatch && taggedMatch[2]) {
    console.log('Found tagged dollar quoting, body:', `${taggedMatch[2].substring(0, 100)}...`)
    return taggedMatch[2].trim()
  }
  
  // PostgreSQL-style: Handle double dollar quoting: AS $$ ... $$
  const doubleDollarMatch = definition.match(/AS\s+\$\$\s*([\s\S]*?)\s*\$\$/i)
  if (doubleDollarMatch && doubleDollarMatch[1]) {
    console.log('Found double dollar quoting, body:', `${doubleDollarMatch[1].substring(0, 100)}...`)
    return doubleDollarMatch[1].trim()
  }
  
  // PostgreSQL-style: Handle single dollar quoting: AS $ ... $
  const singleDollarMatch = definition.match(/AS\s+\$\s*([\s\S]*?)\s*\$(?:\s+LANGUAGE|\s*$)/i)
  if (singleDollarMatch && singleDollarMatch[1]) {
    console.log('Found single dollar quoting, body:', `${singleDollarMatch[1].substring(0, 100)}...`)
    return singleDollarMatch[1].trim()
  }
  
  // Fallback: look for AS followed by quoted content
  const asQuotedMatch = definition.match(/AS\s+['"]([^'"]*)['"]/i)
  if (asQuotedMatch && asQuotedMatch[1]) {
    console.log('Found quoted AS, body:', `${asQuotedMatch[1].substring(0, 100)}...`)
    return asQuotedMatch[1].trim()
  }
  
  // If no AS pattern found, check if this is already just the body
  if (!definition.toUpperCase().includes('CREATE')) {
    console.log('No CREATE found, treating as body:', `${definition.substring(0, 100)}...`)
    return definition.trim()
  }
  
  console.log('Could not extract procedure body')
  return ''
}

// Utility function to extract parameters from CREATE PROCEDURE statement
function extractProcedureParameters(definition: string): ProcedureParameter[] {
  if (!definition) return []
  
  console.log('Extracting procedure parameters from definition')
  
  // Match the parameter list: PROCEDURE name(parameters)
  const paramMatch = definition.match(/CREATE\s+(?:OR\s+REPLACE\s+)?PROCEDURE\s+[^(]+\(([^)]*)\)/i)
  if (!paramMatch || !paramMatch[1]) {
    console.log('No parameters found in procedure definition')
    return []
  }
  
  const paramString = paramMatch[1].trim()
  console.log('Parameter string found:', paramString)
  
  if (!paramString) return []
  
  // Split parameters by comma, but be careful about nested parentheses in types like VARCHAR(255)
  const parameters: ProcedureParameter[] = []
  let current = ''
  let parenLevel = 0
  
  // Process each character to track parentheses nesting
  for (const char of paramString) {
    if (char === '(') {
      parenLevel++
      current += char
    } else if (char === ')') {
      parenLevel--
      current += char
    } else if (char === ',' && parenLevel === 0) {
      if (current.trim()) {
        const param = parseProcedureParameter(current.trim())
        if (param) parameters.push(param)
      }
      current = ''
    } else {
      current += char
    }
  }
  
  // Don't forget the last parameter
  if (current.trim()) {
    const param = parseProcedureParameter(current.trim())
    if (param) parameters.push(param)
  }
  
  console.log('Extracted procedure parameters:', parameters)
  return parameters
}

// Helper function to parse a single parameter string like "IN operation_type character varying" or "OUT result_message text"
function parseProcedureParameter(paramStr: string): ProcedureParameter | null {
  const trimmed = paramStr.trim()
  if (!trimmed) return null
  
  console.log('Parsing parameter:', trimmed)
  
  // Match: [MODE] name type [DEFAULT value] - handle both INOUT and INOUT variants
  const match = trimmed.match(/^(IN|OUT|INOUT)\s+(\w+)\s+(.+?)(?:\s+DEFAULT\s+(.+))?$/i)
  if (!match) {
    console.log('Could not parse parameter:', trimmed)
    return null
  }
  
  const [, mode, name, type, defaultValue] = match
  const modeUpper = (mode?.toUpperCase() || 'IN')
  const safeMode: 'IN' | 'OUT' | 'INOUT' = modeUpper === 'OUT' ? 'OUT' : (modeUpper === 'INOUT' ? 'INOUT' : 'IN')
  const result = {
    name: name || 'param',
    type: type?.trim() || 'VARCHAR(255)',
    mode: safeMode,
    defaultValue: defaultValue?.trim()
  }
  
  console.log('Parsed parameter result:', result)
  return result
}

export function CreateProcedureView({ connection, database, schema, onProcedureCreated, duplicateFrom }: CreateProcedureViewProps) {
  // Initialize state based on whether we're duplicating or creating new
  const [procedureName, setProcedureName] = useState(duplicateFrom?.newName || '')
  const [parameters, setParameters] = useState<ProcedureParameter[]>(() => {
    if (duplicateFrom?.definition) {
      try {
        const def = duplicateFrom.definition as unknown
        console.log('Procedure metadata for parameter extraction:', def)
        
        // Skip metadata approach for procedures since it's incomplete (missing OUT/INOUT modes)
        console.log('*** SKIPPING METADATA APPROACH - known to be incomplete for procedures with OUT/INOUT parameters')
        
        // Fallback: extract parameters directly from SQL definition
        console.log('Metadata approach failed, trying SQL parsing fallback')
        let rawDefinition = ''
        if (def && typeof def === 'object') {
          if ('metadata' in def && def.metadata && typeof def.metadata === 'object' && 'definition' in def.metadata) {
            const maybe = (def.metadata as { definition?: unknown }).definition
            rawDefinition = typeof maybe === 'string' ? maybe : ''
          } else if ('definition' in def) {
            const maybe = (def as { definition?: unknown }).definition
            rawDefinition = typeof maybe === 'string' ? maybe : ''
          }
        } else if (typeof def === 'string') {
          rawDefinition = def
        }
        
        if (rawDefinition) {
          console.log('Using SQL parsing to extract procedure parameters from:', rawDefinition.substring(0, 300))
          return extractProcedureParameters(rawDefinition)
        }
      } catch (error) {
        console.error('Error extracting procedure parameters:', error)
        // Fall through to empty array
      }
    }
    return []
  })
  const [procedureBody, setProcedureBody] = useState(() => {
    if (duplicateFrom?.definition) {
      try {
        const def = duplicateFrom.definition as unknown
        if (def && typeof def === 'object') {
          if ('metadata' in def && def.metadata && typeof def.metadata === 'object' && 'definition' in def.metadata) {
            const maybe = (def.metadata as { definition?: unknown }).definition
            const text = typeof maybe === 'string' ? maybe : ''
            return extractProcedureBody(text)
          }
          if ('definition' in def) {
            const maybe = (def as { definition?: unknown }).definition
            const text = typeof maybe === 'string' ? maybe : ''
            return extractProcedureBody(text)
          }
        }
        if (typeof def === 'string') {
          return extractProcedureBody(def)
        }
      } catch {
        return ''
      }
    }
    return ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const addParameter = () => {
    setParameters([...parameters, { name: '', type: 'VARCHAR(255)', mode: 'IN' }])
  }

  const removeParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index))
  }

  const updateParameter = (index: number, field: keyof ProcedureParameter, value: string) => {
    const newParameters = Array.from(parameters)
    newParameters[index] = { ...newParameters[index], [field]: value }
    setParameters(newParameters)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!procedureName.trim() || !procedureBody.trim()) {
      showToast('error', 'Please fill in procedure name and body')
      return
    }

    setIsSubmitting(true)
    
    try {
      // The backend constructs "CREATE OR REPLACE PROCEDURE schema.name" and appends our definition
      // So we send just the signature and body, without CREATE PROCEDURE procedureName
      const paramString = parameters.map(param => {
        let paramStr = `${param.mode} ${param.name} ${param.type}`
        if (param.defaultValue) {
          paramStr += ` DEFAULT ${param.defaultValue}`
        }
        return paramStr
      }).join(', ')

      const procedureDefinition = isDatabasePostgreSQL
        ? `(${paramString})
LANGUAGE plpgsql
AS $$
${procedureBody}
$$;`
        : `(${paramString})
BEGIN
${procedureBody}
END;`

      console.log('Procedure definition being sent to backend:', procedureDefinition)

      // Create procedure using agent-proxy - send the signature and body (without CREATE PROCEDURE name)
      await AgentProxyService.createProcedure(
        connection.id,
        database,
        {
          procedure_name: procedureName,
          procedure_definition: procedureDefinition
        },
        schema
      )
      
      showToast('success', `Procedure "${procedureName}" created successfully`)
      
      // Invalidate procedures queries to refresh UI
      await queryClient.invalidateQueries({ 
        queryKey: [...databaseKeys.all, 'procedures', connection.id, database, schema] 
      })
      
      onProcedureCreated()
    } catch (error) {
      console.error('Failed to create procedure:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create procedure'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDatabasePostgreSQL = connection.type === 'postgresql'
  const isDatabaseMySQL = connection.type === 'mysql'

  const commonDataTypes = isDatabasePostgreSQL
    ? ['integer', 'bigint', 'text', 'varchar', 'boolean', 'date', 'timestamp', 'numeric', 'json', 'jsonb']
    : ['INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'BOOLEAN', 'DATE', 'DATETIME', 'DECIMAL', 'JSON']

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center">
          <div className="text-2xl mr-3">🔧</div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Procedure
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new stored procedure to encapsulate business logic
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Procedure Name *
                </label>
                <input
                  type="text"
                  value={procedureName}
                  onChange={(e) => { setProcedureName(e.target.value) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="process_orders"
                  required
                />
              </div>
            </div>

            {/* Parameters */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Parameters
                </h3>
                <button
                  type="button"
                  onClick={addParameter}
                  className="flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Add Parameter
                </button>
              </div>

              {parameters.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No parameters defined. Click "Add Parameter" to add procedure parameters.
                </p>
              ) : (
                <div className="space-y-3">
                  {parameters.map((parameter, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {isDatabasePostgreSQL && (
                        <div className="w-20">
                          <select
                            value={parameter.mode}
                            onChange={(e) => { updateParameter(index, 'mode', e.target.value) }}
                            className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          >
                            <option value="IN">IN</option>
                            <option value="OUT">OUT</option>
                            <option value="INOUT">INOUT</option>
                          </select>
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <input
                          type="text"
                          value={parameter.name}
                          onChange={(e) => { updateParameter(index, 'name', e.target.value) }}
                          placeholder="parameter_name"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          required
                        />
                      </div>
                      
                      <div className="w-32">
                        <select
                          value={parameter.type}
                          onChange={(e) => { updateParameter(index, 'type', e.target.value) }}
                          className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          {commonDataTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="w-24">
                        <input
                          type="text"
                          value={parameter.defaultValue || ''}
                          onChange={(e) => { updateParameter(index, 'defaultValue', e.target.value) }}
                          placeholder="Default"
                          className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => { removeParameter(index) }}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Procedure Body */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Procedure Body
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Procedure Definition *
                </label>
                <textarea
                  value={procedureBody}
                  onChange={(e) => { setProcedureBody(e.target.value) }}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder={isDatabasePostgreSQL 
                    ? `-- Your procedure logic here
-- Use parameters like: parameter_name
-- Use COMMIT/ROLLBACK for transaction control

BEGIN
    -- Example business logic
    INSERT INTO audit_log (action, timestamp) 
    VALUES ('procedure_executed', NOW());
    
    -- Conditional logic
    IF parameter1 > 0 THEN
        UPDATE table_name SET status = 'active' WHERE id = parameter1;
    END IF;
    
    COMMIT;
END;`
                    : `-- Your procedure logic here
-- Use parameters like: parameter_name
-- Use COMMIT/ROLLBACK for transaction control

DECLARE done INT DEFAULT FALSE;
DECLARE result_count INT DEFAULT 0;

-- Example business logic
INSERT INTO audit_log (action, timestamp) 
VALUES ('procedure_executed', NOW());

-- Conditional logic
IF parameter1 > 0 THEN
    UPDATE table_name SET status = 'active' WHERE id = parameter1;
    SELECT ROW_COUNT() INTO result_count;
END IF;

SELECT result_count AS affected_rows;`
                  }
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isDatabasePostgreSQL 
                    ? "Write the procedure body in PL/pgSQL. Procedures can perform DML operations and transaction control."
                    : "Write the procedure body in SQL. Procedures can perform DML operations and transaction control."
                  }
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Procedure Guidelines:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Procedures can perform DML operations (INSERT, UPDATE, DELETE)</li>
                    <li>Use IN parameters for input values, OUT for output values</li>
                    <li>Procedures can commit/rollback transactions explicitly</li>
                    <li>Unlike functions, procedures don't return values directly</li>
                    {isDatabasePostgreSQL && <li>PostgreSQL procedures support transaction control with COMMIT/ROLLBACK</li>}
                    {isDatabaseMySQL && <li>MySQL procedures can use conditional logic and loops</li>}
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onProcedureCreated}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Creating...' : 'Create Procedure'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
